import cn from 'classnames';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { nanoid } from 'nanoid';
import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { ResultsContext } from '@webapp/account/src/pages/survey/results/context';
import { Actions, BASE_PERIOD_ID, getPeriodName } from '@webapp/account/src/resources/reports';
import {
    ALL_TIME_PERIOD_NAME,
    ComparisonPreset,
    createAllTimePeriod,
    getPresetPeriod,
    isPresetAvailable,
    MAX_COMPARE_PERIODS,
    PRESET_LABELS,
} from '@webapp/account/src/resources/reports/comparison';
import type { ComparisonPeriod } from '@webapp/account/src/resources/reports/comparison';
import { selectUserChartsSettings } from '@webapp/account/src/resources/settings/selectors';

import { PencilEdit, Remove, TinyPlus } from '@webapp/common/assets/icons';
import { Button } from '@webapp/common/components/button';
import { getPeriodColors, SCHEMES } from '@webapp/common/components/charts/utils';
import { Checkbox } from '@webapp/common/components/checkbox';
import { DatePicker } from '@webapp/common/components/date-picker';
import { EditName } from '@webapp/common/components/edit-name';
import { Modal } from '@webapp/common/components/modal';
import { Switch } from '@webapp/common/components/switch';
import { Textfield, TextfieldType } from '@webapp/common/components/textfield';
import { useAction } from '@webapp/common/hooks/use-actions';
import { DATE_FORMAT, dateFormat2IMask, PICKER_DATE_FORMAT } from '@webapp/common/lib/date';

import css from './comparison-periods-modal.css';

interface DraftPeriod {
    id: string;
    dateFrom: string;
    dateTo: string;
    name?: string;
    preset?: ComparisonPreset;
}

const PRESETS = [ComparisonPreset.PREV_SIMILAR, ComparisonPreset.PREV_MONTH, ComparisonPreset.PREV_YEAR];

const isFilled = (period: DraftPeriod): boolean => Boolean(period.dateFrom && period.dateTo);

const toDraft = (period: ComparisonPeriod): DraftPeriod => ({
    id: period.id,
    dateFrom: period.dateFrom,
    dateTo: period.dateTo,
    name: period.name,
    preset: period.preset,
});

export const ComparisonPeriodsModal: FC<{
    minDate: string;
    onClose: () => void;
}> = ({ minDate, onClose }) => {
    const { globalFilters } = useContext(ResultsContext);
    const patchFilters = useAction(Actions.patchFilters);
    const renameComparisonPeriod = useAction(Actions.renameComparisonPeriod);
    const userChartsSettings = useSelector(selectUserChartsSettings);
    const chartColorSchemes = userChartsSettings.chartColorSchemes || SCHEMES;

    const [baseRange, setBaseRange] = useState({
        from: dayjs(globalFilters.dateFrom).format(DATE_FORMAT),
        to: dayjs(globalFilters.dateTo).format(DATE_FORMAT),
    });
    const [baseName, setBaseName] = useState(globalFilters.basePeriodName || '');
    const [draft, setDraft] = useState<Array<DraftPeriod>>(() => (globalFilters.comparePeriods || []).map(toDraft));
    const [editingId, setEditingId] = useState<string>(null);

    const colors = getPeriodColors(draft.length + 1, chartColorSchemes);
    const maxReached = draft.length >= MAX_COMPARE_PERIODS;
    const allTimeOn = draft.some(({ preset }) => preset === ComparisonPreset.ALL_TIME);

    // Пресет, который целиком попадает до даты создания опроса, добавить нельзя — данных не будет.
    const presetAvailable = useCallback(
        (preset: ComparisonPreset): boolean => {
            const base = { dateFrom: baseRange.from, dateTo: baseRange.to };

            if (!isPresetAvailable({ base, preset })) {
                return false;
            }

            const period = getPresetPeriod({ base, preset, minDate });

            return !dayjs(period.dateTo).isBefore(dayjs(minDate), 'day');
        },
        [baseRange, minDate],
    );

    const setRange = useCallback((id: string, range: { from: string; to: string }) => {
        if (id === BASE_PERIOD_ID) {
            setBaseRange(range);
            return;
        }

        setDraft((periods) =>
            periods.map((period) =>
                period.id === id
                    ? { ...period, dateFrom: range.from, dateTo: range.to, preset: ComparisonPreset.CUSTOM }
                    : period,
            ),
        );
    }, []);

    const rename = useCallback((id: string, name: string) => {
        if (id === BASE_PERIOD_ID) {
            setBaseName(name);
            return;
        }

        setDraft((periods) => periods.map((period) => (period.id === id ? { ...period, name } : period)));
    }, []);

    const removePeriod = useCallback((id: string) => {
        setDraft((periods) => periods.filter((period) => period.id !== id));
    }, []);

    const addPreset = useCallback(
        (preset: ComparisonPreset) => {
            if (maxReached) {
                return;
            }

            const period = getPresetPeriod({
                base: { dateFrom: baseRange.from, dateTo: baseRange.to },
                preset,
                minDate,
            });

            setDraft((periods) => {
                if (periods.some(({ preset: current }) => current === preset)) {
                    return periods;
                }

                const emptyIndex = periods.findIndex((item) => !isFilled(item));

                if (emptyIndex > -1) {
                    return periods.map((item, index) =>
                        index === emptyIndex ? { ...toDraft(period), id: item.id, name: item.name } : item,
                    );
                }

                return [...periods, toDraft(period)];
            });
        },
        [baseRange, maxReached, minDate],
    );

    const addEmpty = useCallback(() => {
        if (maxReached) {
            return;
        }

        setDraft((periods) => [...periods, { id: nanoid(), dateFrom: '', dateTo: '' }]);
    }, [maxReached]);

    const toggleAllTime = useCallback(
        (checked: boolean) => {
            if (checked) {
                setDraft((periods) =>
                    periods.some(({ preset }) => preset === ComparisonPreset.ALL_TIME)
                        ? periods
                        : [...periods, toDraft({ ...createAllTimePeriod(minDate), name: ALL_TIME_PERIOD_NAME })],
                );
            } else {
                setDraft((periods) => periods.filter(({ preset }) => preset !== ComparisonPreset.ALL_TIME));
            }
        },
        [minDate],
    );

    const clearAll = useCallback(() => {
        setDraft([]);
    }, []);

    const apply = useCallback(() => {
        const filled = draft.filter(isFilled);
        const datesChanged = baseRange.from !== globalFilters.dateFrom || baseRange.to !== globalFilters.dateTo;
        const structureChanged =
            datesChanged ||
            !isEqual(
                filled.map(({ dateFrom, dateTo, preset }) => ({ dateFrom, dateTo, preset })),
                (globalFilters.comparePeriods || []).map(({ dateFrom, dateTo, preset }) => ({
                    dateFrom,
                    dateTo,
                    preset,
                })),
            );

        if (structureChanged) {
            patchFilters({
                comparePeriods: filled,
                basePeriodName: baseName.trim() || undefined,
                dateFrom: baseRange.from,
                dateTo: baseRange.to,
            });
        } else {
            renameComparisonPeriod({ id: BASE_PERIOD_ID, name: baseName });
            filled.forEach(({ id, name }) => renameComparisonPeriod({ id, name }));
        }

        onClose();
    }, [
        baseName,
        baseRange,
        draft,
        globalFilters.comparePeriods,
        globalFilters.dateFrom,
        globalFilters.dateTo,
        onClose,
        patchFilters,
        renameComparisonPeriod,
    ]);

    const presetChecked = (preset: ComparisonPreset): boolean =>
        draft.some((period) => period.preset === preset && isFilled(period));
    const hasEmptyPeriod = draft.some((period) => !isFilled(period));

    const togglePreset = (preset: ComparisonPreset, checked: boolean) => {
        if (checked) {
            addPreset(preset);
            return;
        }

        setDraft((periods) => periods.filter((period) => period.preset !== preset));
    };

    const toggleEmpty = (checked: boolean) => {
        if (checked) {
            addEmpty();
            return;
        }

        setDraft((periods) => periods.filter((period) => isFilled(period)));
    };

    const renderLabel = (id: string, name: string | undefined, index: number) => {
        if (editingId === id) {
            return (
                <EditName
                    className={css.nameEdit}
                    name={name || ''}
                    placeholder={getPeriodName({ name: undefined }, index)}
                    onDone={() => setEditingId(null)}
                    onSave={(value) => rename(id, value)}
                />
            );
        }

        return (
            <div className={cn(css.itemName, css.itemNameHoverable)} onClick={() => setEditingId(id)}>
                <span className={css.itemNameText} title='Переименовать период'>
                    {getPeriodName({ name }, index)}
                </span>
                <PencilEdit className={css.itemNameIcon} />
            </div>
        );
    };

    const renderRange = (id: string, period: { dateFrom: string; dateTo: string }) => (
        <div className={css.itemRange}>
            <DatePicker
                customInput={
                    <Textfield
                        mask={dateFormat2IMask(PICKER_DATE_FORMAT)}
                        placeholder='Выберите дату'
                        type={TextfieldType.DATE}
                    />
                }
                dateFormat={PICKER_DATE_FORMAT}
                minDate={dayjs(minDate)}
                selected={period.dateFrom ? dayjs(period.dateFrom).toDate() : undefined}
                onChange={(date) => date && setRange(id, { from: dayjs(+date).format(DATE_FORMAT), to: period.dateTo })}
            />
            <span className={css.dash}>—</span>
            <DatePicker
                customInput={
                    <Textfield
                        mask={dateFormat2IMask(PICKER_DATE_FORMAT)}
                        placeholder='Выберите дату'
                        type={TextfieldType.DATE}
                    />
                }
                dateFormat={PICKER_DATE_FORMAT}
                minDate={period.dateFrom ? dayjs(period.dateFrom) : dayjs(minDate)}
                selected={period.dateTo ? dayjs(period.dateTo).toDate() : undefined}
                onChange={(date) =>
                    date && setRange(id, { from: period.dateFrom, to: dayjs(+date).format(DATE_FORMAT) })
                }
            />
        </div>
    );

    return (
        <Modal className={css.modal} visible={true} onClose={onClose}>
            <div className={css.title}>Сравнение периодов</div>
            <div className={css.body}>
                <div className={css.main}>
                    <div className={css.list}>
                        <div className={css.item}>
                            <div className={css.itemHead}>
                                <span className={css.marker} style={{ backgroundColor: colors[0] }} />
                                {renderLabel(BASE_PERIOD_ID, baseName || undefined, 0)}
                            </div>
                            {renderRange(BASE_PERIOD_ID, { dateFrom: baseRange.from, dateTo: baseRange.to })}
                        </div>
                        {draft.map((period, index) => (
                            <div className={css.item} key={period.id}>
                                <div className={css.itemHead}>
                                    <span className={css.marker} style={{ backgroundColor: colors[index + 1] }} />
                                    {renderLabel(period.id, period.name, index + 1)}
                                    <Remove className={css.remove} onClick={() => removePeriod(period.id)} />
                                </div>
                                {renderRange(period.id, period)}
                            </div>
                        ))}
                    </div>
                    <div className={css.addRow}>
                        <button type='button' className={css.addBtn} disabled={maxReached} onClick={addEmpty}>
                            <TinyPlus className={css.addIcon} />
                            Добавить период
                        </button>
                        <span className={css.hint}>max {MAX_COMPARE_PERIODS} периода</span>
                    </div>
                </div>
                <div className={css.aside}>
                    <div className={css.asideTitle}>Быстрый выбор периодов</div>
                    <div className={css.presets}>
                        {PRESETS.map((preset) => {
                            const checked = presetChecked(preset);

                            return (
                                <Checkbox
                                    checked={checked}
                                    disabled={!checked && (maxReached || !presetAvailable(preset))}
                                    key={preset}
                                    label={PRESET_LABELS[preset]}
                                    onChange={(value) => togglePreset(preset, value)}
                                />
                            );
                        })}
                        <Checkbox
                            checked={hasEmptyPeriod}
                            disabled={!hasEmptyPeriod && maxReached}
                            label={PRESET_LABELS[ComparisonPreset.CUSTOM]}
                            onChange={toggleEmpty}
                        />
                    </div>
                    <hr className={css.divider} />
                    <Switch
                        checked={allTimeOn}
                        disabled={!allTimeOn && maxReached}
                        label={<>Добавить период «{ALL_TIME_PERIOD_NAME}»</>}
                        onChange={toggleAllTime}
                        tooltip='В сравнение будет добавлен общий период за всё время.'
                    />
                </div>
            </div>
            <div className={css.footer}>
                <Button color='white' onClick={clearAll}>
                    Очистить все
                </Button>
                <div className={css.footerRight}>
                    <Button color='white' onClick={onClose}>
                        Отмена
                    </Button>
                    <Button color='blue' onClick={apply}>
                        Применить
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
